import ArrowCircleDownIcon from "@mui/icons-material/ArrowCircleDown";
import ArrowCircleUpIcon from "@mui/icons-material/ArrowCircleUp";
import RemoveIcon from "@mui/icons-material/Remove";
import { Avatar, Box, Grid, IconButton, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DataGridPro } from "@mui/x-data-grid-pro";
import clsx from "clsx";
import _ from "lodash";
import PropTypes from "prop-types";
import React from "react";
import customLoadingTableOverlay from "../../components/CustomLoadingTableOverlay";
import CustomNoRowsTableOverlay from "../../components/CustomNoRowsTableOverlay";
import { BID_CHOICES_PAGE_TYPE } from "../../util/constants";
import CustomErrorTableOverlay from "../CustomErrorTableOverlay.jsx";
import { BID_AWARD_TYPE } from "../../util/bid/bidConstants";
import { getShiftJobType } from "../../util/bid/shiftBidUtils";

const useStyles = makeStyles((theme) => ({
  bidDayColumn: {
    whiteSpace: "pre-wrap",
    textAlign: "left",
  },
  tableStyle: {
    width: "100%",
    height: "inherit",
  },
  mobileTableScroll: {
    width: "100%",
    height: `calc(100% - 5px)`,
    overflowX: "scroll",
  },
}));

const ShiftBidReviewTable = (props) => {
  const classes = useStyles();
  const getValueBidLineDayCell = (row, dayOfWeek) => {
    const shift = row.shifts.find((shift) => shift.day === dayOfWeek);
    if (shift.isOff) {
      return "Off";
    } else {
      const shiftTime = `${shift.start}-${shift.end}`;
      const shiftJobType = getShiftJobType(shift, props.useAssignmentForEmp);
      return `${shiftTime} ${shiftJobType}`;
    }
  };

  const renderBidLineDayCell = (row, dayOfWeek) => {
    const shift = row.shifts.find((shift) => shift.day === dayOfWeek);
    if (shift.isOff) {
      return <Box className={classes.bidDayColumn}>Off</Box>;
    } else {
      const shiftTime = `${shift.start}-${shift.end}`;
      const shiftJobType = getShiftJobType(shift, props.useAssignmentForEmp);
      return (
        <Box className={classes.bidDayColumn}>
          <Typography sx={{ fontSize: "14px" }}>{shiftTime}</Typography>
          <Typography sx={{ fontSize: "10px" }}>{shiftJobType}</Typography>
        </Box>
      );
    }
  };

  const renderBidLineShiftDiffCell = (row) => {
    if (row.shiftDiff && row.shiftDiff !== "" && row.shiftDiff !== "0") {
      return (
        <Box className={classes.bidDayColumn}>
          <Typography sx={{ fontSize: "14px" }}>{row.lineNumber}</Typography>
          <Typography sx={{ fontSize: "10px", color: "primary.swaBlue" }}>${row.shiftDiff}</Typography>
        </Box>
      );
    } else {
      return row.lineNumber;
    }
  };

  const renderActionsArrowUp = (value) => {
    return (
      <Grid width={10}>
        <IconButton
          sx={{ padding: 0 }}
          disabled={value.choice === 1}
          onClick={async () => {
            if (value.choice > 1) {
              const newRows = updateRowPosition(value.choice - 1, value.choice - 2, props.selectedBidLines);
              let updatedRows = newRows.map((item, index) => {
                return { ...item, choice: index + 1 };
              });
              let updatedChoices = [];
              updatedChoices = newRows.map((obj) => obj.id);
              props.setSelectedChoices(updatedChoices);
              props.setSelectedBidLines(updatedRows);
            }
          }}
        >
          <ArrowCircleUpIcon color="primary" />
        </IconButton>
      </Grid>
    );
  };
  const renderActionsArrowDown = (value) => {
    return (
      <Grid width={10}>
        <IconButton
          sx={{ padding: 0 }}
          disabled={value.choice === props.selectedBidLines?.length}
          onClick={async () => {
            if (value.choice !== props.selectedBidLines?.length) {
              const newRows = updateRowPosition(value.choice - 1, value.choice, props.selectedBidLines);
              let updatedRows = newRows.map((item, index) => {
                return { ...item, choice: index + 1 };
              });
              let updatedChoices = [];
              updatedChoices = newRows.map((obj) => obj.id);
              props.setSelectedChoices(updatedChoices);
              props.setSelectedBidLines(updatedRows);
            }
          }}
        >
          <ArrowCircleDownIcon color="primary" />
        </IconButton>
      </Grid>
    );
  };

  const getBlockLineNameRow = (params) => {
    return params.row.isBlockLine &&
      Object.hasOwn(params.row, "assignedEmployee") &&
      !_.isEmpty(params.row.assignedEmployee) ? (
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "secondary.swaGray2",
          height: "100%",
          backgroundColor: "secondary.swaLightestBlue",
        }}
      >
        <Box
          sx={{
            display: "flex",
            bgcolor: "secondary.swaLightestBlue",
            height: "100%",
            paddingLeft: "10px",
            alignItems: "center",
            marginLeft: "7rem",
          }}
        >
          <Typography sx={{ fontSize: "12px" }}>
            Block Line (Bid Line {params.row.lineNumber}):{" "}
            <b>
              {params.row.assignedEmployee.firstName} {params.row.assignedEmployee.lastName}
            </b>
          </Typography>
        </Box>
      </Box>
    ) : (
      <></>
    );
  };
  const updateRowPosition = (initialIndex, newIndex, rows) => {
    const rowsClone = [...rows];
    const row = rowsClone.splice(initialIndex, 1)[0];
    rowsClone.splice(newIndex, 0, row);
    return rowsClone;
  };

  const getBidLineMobileColumns = () => {
    let bidLineMobileColumns = [];
    if (!props.isReadOnly) {
      bidLineMobileColumns.push(
        {
          field: "arrowup",
          headerName: "",
          headerAlign: "left",
          align: "left",
          type: "string",
          width: 44,
          minWidth: 25,
          sortable: false,
          className: classes.blueColumn,
          renderCell: (params) => {
            return renderActionsArrowUp(params.row);
          },
        },
        {
          field: "arrowdown",
          headerName: "",
          headerAlign: "left",
          align: "left",
          type: "string",
          width: 44,
          minWidth: 25,
          sortable: false,
          className: classes.blueColumn,
          renderCell: (params) => {
            return renderActionsArrowDown(params.row);
          },
        }
      );
    }

    bidLineMobileColumns.push({
      field: "choices",
      headerName: "Choice",
      renderCell: (params, idx) => {
        return (
          props.selectedChoices?.includes(params.row.lineNumber) && (
            <Box>
              <Avatar
                sx={{
                  bgcolor: "primary.swaBlue",
                  width: 32,
                  height: 32,
                }}
              >
                {findChoiceIndex(params.row.lineNumber)}
              </Avatar>
            </Box>
          )
        );
      },
      width: 56,
      align: "center",
      cellClassName: (params) => {
        return clsx("choices", {
          selected: props.selectedChoices?.includes(params.row.lineNumber),
        });
      },
      sortable: false,
    });

    if (!props.isReadOnly) {
      bidLineMobileColumns.push({
        field: "removeButton",
        headerName: "",
        renderCell: (params, idx) => {
          return (
            props.selectedChoices?.includes(params.row.lineNumber) && (
              <IconButton
                onClick={() => {
                  handleRemoveChoice(params.row.lineNumber);
                }}
              >
                <RemoveIcon sx={{ color: "primary.swaBlue" }} />
              </IconButton>
            )
          );
        },
        width: 56,
        align: "center",
        sortable: false,
      });
    }

    bidLineMobileColumns.push(
      {
        field: "lineNumberAndDifferential",
        headerName: "Bid Line",
        renderCell: (params, idx) => {
          return renderBidLineShiftDiffCell(params.row);
        },
        width: "65",
        sortable: false,
      },
      {
        field: "Sunday",
        headerName: "Sun",
        renderCell: (params) => {
          return renderBidLineDayCell(params.row, "Sunday");
        },
        valueGetter: (params) => {
          return getValueBidLineDayCell(params.row, "Sunday");
        },
        width: "70",
        sortable: false,
      },
      {
        field: "Monday",
        headerName: "Mon",
        renderCell: (params) => {
          return renderBidLineDayCell(params.row, "Monday");
        },
        valueGetter: (params) => {
          return getValueBidLineDayCell(params.row, "Monday");
        },
        width: "70",
        sortable: false,
      },
      {
        field: "Tuesday",
        headerName: "Tue",
        renderCell: (params) => {
          return renderBidLineDayCell(params.row, "Tuesday");
        },
        valueGetter: (params) => {
          return getValueBidLineDayCell(params.row, "Tuesday");
        },
        width: "70",
        sortable: false,
      },
      {
        field: "Wednesday",
        headerName: "Wed",
        renderCell: (params) => {
          return renderBidLineDayCell(params.row, "Wednesday");
        },
        valueGetter: (params) => {
          return getValueBidLineDayCell(params.row, "Wednesday");
        },
        width: "70",
        sortable: false,
      },
      {
        field: "Thursday",
        headerName: "Thu",
        renderCell: (params) => {
          return renderBidLineDayCell(params.row, "Thursday");
        },
        valueGetter: (params) => {
          return getValueBidLineDayCell(params.row, "Thursday");
        },
        width: "70",
        sortable: false,
      },
      {
        field: "Friday",
        headerName: "Fri",
        renderCell: (params) => {
          return renderBidLineDayCell(params.row, "Friday");
        },
        valueGetter: (params) => {
          return getValueBidLineDayCell(params.row, "Friday");
        },
        width: "70",
        sortable: false,
      },
      {
        field: "Saturday",
        headerName: "Sat",
        renderCell: (params) => {
          return renderBidLineDayCell(params.row, "Saturday");
        },
        valueGetter: (params) => {
          return getValueBidLineDayCell(params.row, "Saturday");
        },
        width: "70",
        sortable: false,
      }
    );

    return bidLineMobileColumns;
  };

  const getBidLineDesktopColumns = () => {
    let bidLineDesktopColumns = [];
    if (!props.isReadOnly) {
      bidLineDesktopColumns.push(
        {
          field: "arrowup",
          headerName: "",
          headerAlign: "left",
          align: "left",
          type: "string",
          width: 44,
          minWidth: 25,
          sortable: false,
          className: classes.blueColumn,
          renderCell: (params) => {
            return renderActionsArrowUp(params.row);
          },
        },
        {
          field: "arrowdown",
          headerName: "",
          headerAlign: "left",
          align: "left",
          type: "string",
          width: 44,
          minWidth: 25,
          sortable: false,
          className: classes.blueColumn,
          renderCell: (params) => {
            return renderActionsArrowDown(params.row);
          },
        }
      );
    }

    bidLineDesktopColumns.push({
      field: "choices",
      headerName: "Choice",
      renderCell: (params, idx) => {
        return (
          props.selectedChoices?.includes(params.row.lineNumber) && (
            <Box>
              <Avatar
                sx={{
                  bgcolor: "primary.swaBlue",
                  width: 32,
                  height: 32,
                }}
              >
                {findChoiceIndex(params.row.lineNumber)}
              </Avatar>
            </Box>
          )
        );
      },
      flex: 0.3,
      align: "center",
      cellClassName: (params) => {
        return clsx("choices", {
          selected: props.selectedChoices?.includes(params.row.lineNumber),
        });
      },
      sortable: false,
    });

    if (!props.isReadOnly) {
      bidLineDesktopColumns.push({
        field: "removeButton",
        headerName: "",
        renderCell: (params, idx) => {
          return (
            props.selectedChoices?.includes(params.row.lineNumber) && (
              <IconButton
                onClick={() => {
                  handleRemoveChoice(params.row.lineNumber);
                }}
              >
                <RemoveIcon sx={{ color: "primary.swaBlue" }} />
              </IconButton>
            )
          );
        },
        width: 56,
        align: "center",
        sortable: false,
      });
    }

    bidLineDesktopColumns.push(
      {
        field: "lineNumberAndDifferential",
        headerName: "Bid Line",
        renderCell: (params, idx) => {
          return renderBidLineShiftDiffCell(params.row);
        },
        flex: 0.4,
        sortable: false,
      },
      {
        field: "Sunday",
        headerName: "Sun",
        renderCell: (params) => {
          return renderBidLineDayCell(params.row, "Sunday");
        },
        valueGetter: (params) => {
          return getValueBidLineDayCell(params.row, "Sunday");
        },
        flex: 0.75,
        sortable: false,
      },
      {
        field: "Monday",
        headerName: "Mon",
        renderCell: (params) => {
          return renderBidLineDayCell(params.row, "Monday");
        },
        valueGetter: (params) => {
          return getValueBidLineDayCell(params.row, "Monday");
        },
        flex: 0.75,
        sortable: false,
      },
      {
        field: "Tuesday",
        headerName: "Tue",
        renderCell: (params) => {
          return renderBidLineDayCell(params.row, "Tuesday");
        },
        valueGetter: (params) => {
          return getValueBidLineDayCell(params.row, "Tuesday");
        },
        flex: 0.75,
        sortable: false,
      },
      {
        field: "Wednesday",
        headerName: "Wed",
        renderCell: (params) => {
          return renderBidLineDayCell(params.row, "Wednesday");
        },
        valueGetter: (params) => {
          return getValueBidLineDayCell(params.row, "Wednesday");
        },
        flex: 0.75,
        sortable: false,
      },
      {
        field: "Thursday",
        headerName: "Thu",
        renderCell: (params) => {
          return renderBidLineDayCell(params.row, "Thursday");
        },
        valueGetter: (params) => {
          return getValueBidLineDayCell(params.row, "Thursday");
        },
        flex: 0.75,
        sortable: false,
      },
      {
        field: "Friday",
        headerName: "Fri",
        renderCell: (params) => {
          return renderBidLineDayCell(params.row, "Friday");
        },
        valueGetter: (params) => {
          return getValueBidLineDayCell(params.row, "Friday");
        },
        flex: 0.75,
        sortable: false,
      },
      {
        field: "Saturday",
        headerName: "Sat",
        renderCell: (params) => {
          return renderBidLineDayCell(params.row, "Saturday");
        },
        valueGetter: (params) => {
          return getValueBidLineDayCell(params.row, "Saturday");
        },
        flex: 0.75,
        sortable: false,
      }
    );

    return bidLineDesktopColumns;
  };

  const findChoiceIndex = (bidLine) => {
    return props.selectedChoices.indexOf(bidLine) + 1;
  };

  const handleRemoveChoice = (bidLineNumber) => {
    let copiedSelectedChoices = [...props.selectedChoices];
    copiedSelectedChoices = copiedSelectedChoices.filter((item) => item !== bidLineNumber);
    props.setSelectedChoices(copiedSelectedChoices);
    let copiedSelectedBidLines = [...props.selectedBidLines];
    copiedSelectedBidLines = copiedSelectedBidLines.filter((bidLine) =>
      copiedSelectedChoices.includes(bidLine.lineNumber)
    );
    props.setSelectedBidLines(copiedSelectedBidLines);
  };

  return (
    <div className={props.isDesktopView ? classes.tableStyle : classes.mobileTableScroll}>
      <DataGridPro
        columns={props.isDesktopView ? getBidLineDesktopColumns() : getBidLineMobileColumns()}
        rows={props.selectedBidLines}
        headerHeight={50}
        rowHeight={50}
        getRowHeight={() => "auto"}
        density="compact"
        components={{
          LoadingOverlay: customLoadingTableOverlay,
          NoRowsOverlay: CustomNoRowsTableOverlay,
          ErrorOverlay: CustomErrorTableOverlay,
          Toolbar: null,
        }}
        componentsProps={{
          noRowsOverlay: { pageType: BID_CHOICES_PAGE_TYPE },
        }}
        loading={props.isBidLinesLoading}
        disableColumnMenu={true}
        showColumnVerticalBorder={true}
        showCellVerticalBorder={true}
        showCellRightBorder={true}
        initialState={{
          columns: {
            columnVisibilityModel: {
              __detail_panel_toggle__: false,
            },
          },
          pinnedColumns: {
            left: ["arrowup", "arrowdown", "choices", "removeButton", "lineNumberAndDifferential"],
          },
        }}
        sx={{
          border: 0,
          "& .MuiDataGrid-footerContainer": {
            display: "none",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            color: "primary.swaBlue",
            fontWeight: "bold",
          },
          "& .MuiDataGrid-columnHeader": {
            padding: "0 4px 0 4px",
          },
          "& .offCell": {
            backgroundColor: "secondary.swaGray2",
          },
          "& .blockedBidLine": {
            backgroundColor: "secondary.swaLightestBlue",
          },
          "& .selectedAwardedBid": {
            backgroundColor: "primary.swaYellow",
          },
        }}
        getCellClassName={(params) => {
          // Shift Bid - AA & IAO-AA
          if (
            (props.selectedAwardedBid?.awardType === BID_AWARD_TYPE.AUTO_AWARDED &&
              params.row.lineNumber === props.selectedAwardedBid.lineNumber) ||
            (props.selectedAwardedBid?.awardType === BID_AWARD_TYPE.INELIGIBLE_AT_OPEN_AUTO_AWARDED &&
              params.row.lineNumber === Math.floor(props.selectedAwardedBid.lineNumber))
          ) {
            return "selectedAwardedBid";
          }
          if (params.row.isBlockLine) {
            return "blockedBidLine";
          }
          if (params.value === "Off") {
            return "offCell";
          }
        }}
        getDetailPanelContent={(params) => getBlockLineNameRow(params)}
        getDetailPanelHeight={() => 25}
        detailPanelExpandedRowIds={props.selectedBidLines
          .filter(
            (item) => item.isBlockLine && Object.hasOwn(item, "assignedEmployee") && !_.isEmpty(item.assignedEmployee)
          )
          .map((item) => item.lineNumber)}
      />{" "}
    </div>
  );
};

ShiftBidReviewTable.propTypes = {
  selectedBidLines: PropTypes.array,
  isBidLinesLoading: PropTypes.bool.isRequired,
  selectedChoices: PropTypes.array.isRequired,
  setSelectedChoices: PropTypes.func.isRequired,
  setSelectedBidLines: PropTypes.func.isRequired,
  isDesktopView: PropTypes.bool,
  isReadOnly: PropTypes.bool.isRequired,
  selectedAwardedBid: PropTypes.object,
  useAssignmentForEmp: PropTypes.bool.isRequired,
};

export default ShiftBidReviewTable;
